/**
 * Le foto della commessa (GI-237): si sfogliano i fotogrammi, si vede che cosa
 * c'e' gia' sopra, e si dichiara a mano cio' che il modello non ha visto.
 *
 * **Tre colonne, come la mappa, e la pagina non scorre.** A sinistra l'elenco
 * con filtro per camera e per nome; al centro il fotogramma con le anomalie
 * gia' registrate disegnate sopra; a destra la scheda del fotogramma e, sotto
 * un pannello che si apre, la dichiarazione a mano. Il vecchio modo «ricerca
 * libera» era questa stessa schermata senza miniature, senza filtri e senza
 * le anomalie sul fotogramma: un elenco di nomi accanto a un rettangolo nero.
 *
 * **E' il modo in cui entra nel prodotto cio' che il modello non ha visto.**
 * Senza questa schermata la consegna contiene esattamente quello che il modello
 * ha trovato, e le anomalie mancate restano sul terreno — dove contano.
 *
 * **Il gesto e' lo stesso del banco di lavoro**: si traccia il riquadro
 * trascinando, **dopo aver acceso lo strumento** (`B`, o il bottone sulla
 * tela). Finche' lo strumento e' spento il trascinamento sposta il fotogramma
 * e la rotella lo ingrandisce — com'e' ovunque. Il disegno stava sempre
 * acceso, e il fotogramma non si poteva ne' spostare ne' ingrandire: ogni
 * trascinamento era un riquadro, e nessuno lo diceva (visto dal vivo
 * l'8.9.2026). Il trascinamento resta una *scorciatoia*: riempie i quattro
 * campi numerici, che restano visibili e modificabili, perche' pretendere una
 * precisione al pixel col puntatore escluderebbe da questo lavoro chi non lo usa.
 *
 * **La scrittura passa dalla casella d'uscita (GI-169).** E' un intento
 * `manual_finding` — la stessa strada dei verdetti, con la stessa idempotenza e
 * la stessa riconciliazione — e l'anomalia si dichiara **su un elemento della
 * rete**, mai su un fotogramma da solo.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { ServiceError } from '../api'
import * as coverage from '../coverage'
import { boxOf, draftOf, levelsOf, NO_BOX } from '../coverage'
import type { BoxDraft } from '../coverage'
import { rankOf } from '../design/scales'
import { extentOf, extentText, qualityFact, takenFact } from './facts'
import { outbox } from '../outbox'
import { indexPath, prewarm } from '../pyramid'
import type { IntentState, Rejected } from '../outbox'
import { subjectOf } from '../reviewProtocol'
import { HEARTBEAT_MS, heartbeat, openSession } from '../reviewSessions'
import { parseReviewSessionId } from '../router'
import type { CameraCount, Code, CoverageElement, Frame, FrameFinding } from '../types'
import { Viewer } from '../Viewer'
import type { Mark } from '../Viewer'
import { CanvasTools } from '../cockpit/CanvasTools'
import type { Action } from '../keys'
import { actionFor, commanding, editable } from '../keys'
import { useT } from '../i18n/LocaleProvider'
import './photos.css'

export interface PhotosViewProps {
  inspectionId: string
}

/** I filtri della colonna sinistra: `null` e' «tutti», non un filtro. */
interface Filters {
  camera: string | null
  reviewed: boolean | null
  noCandidates: boolean | null
  q: string | null
  /** Solo i fotogrammi che inquadrano questo elemento: arriva dall'indirizzo,
   *  dalla copertura o dalla scheda dell'elemento. */
  element: string | null
}

const NO_FILTERS: Filters = { camera: null, reviewed: null, noCandidates: null, q: null, element: null }

export function PhotosView({ inspectionId }: PhotosViewProps): JSX.Element {
  const t = useT()
  const location = useLocation()
  const [frames, setFrames] = useState<Frame[]>([])
  const [total, setTotal] = useState(0)
  const [cameras, setCameras] = useState<CameraCount[]>([])
  const [filters, setFilters] = useState<Filters>(() => ({
    ...NO_FILTERS,
    element: new URLSearchParams(location.search).get('element'),
  }))
  // Il nome puo' arrivare dall'indirizzo: e' come l'elenco delle anomalie
  // manda qui «il fotogramma di questa anomalia».
  const [typed, setTyped] = useState(() => new URLSearchParams(location.search).get('q') ?? '')
  const [offset, setOffset] = useState(0)
  const [reloads, setReloads] = useState(0)
  const [chosen, setChosen] = useState<string | null>(null)
  const [findings, setFindings] = useState<FrameFinding[]>([])
  const [elements, setElements] = useState<CoverageElement[]>([])
  const [codes, setCodes] = useState<Code[]>([])
  const [draft, setDraft] = useState<BoxDraft>(NO_BOX)
  // Lo strumento del riquadro: acceso si disegna, spento si sposta e si
  // ingrandisce. Si spegne da solo quando il riquadro e' chiuso, come il
  // contorno nel banco: il trascinamento dopo deve tornare a spostare.
  const [sketching, setSketching] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [view, setView] = useState<{ how: 'uno-a-uno' | 'inquadra' | 'trovata'; nonce: number } | null>(null)
  // Il pannello della dichiarazione si apre da solo quando si e' disegnato:
  // il riquadro riempie quattro campi che altrimenti non si vedrebbero.
  const [declaring, setDeclaring] = useState(false)
  const [elementId, setElementId] = useState('')
  const [code, setCode] = useState('')
  const [level, setLevel] = useState(2)
  const [failure, setFailure] = useState<ServiceError | null>(null)
  const [wrong, setWrong] = useState<string | null>(null)
  const [said, setSaid] = useState<string | null>(null)
  const [unsent, setUnsent] = useState(0)
  const [refused, setRefused] = useState<Rejected[]>([])
  const [declarations, setDeclarations] = useState<Map<string, IntentState>>(new Map())
  const [session, setSession] = useState<string | null>(parseReviewSessionId(location.search))
  const [epoch, setEpoch] = useState(0)

  const now = useRef<{ sessionId: string; reviewEpoch: number } | null>(null)
  useEffect(() => {
    now.current = session === null ? null : { sessionId: session, reviewEpoch: epoch }
  }, [session, epoch])

  // Una sola casella d'uscita per tutta la schermata: crearne una a ogni
  // ridisegno perderebbe le dichiarazioni ancora da consegnare.
  const posta = useMemo(
    () =>
      outbox({
        onChange: setUnsent,
        onRejected: (one) => setRefused((were) => [...were, one]),
        session: () => now.current,
      }),
    [],
  )

  function onFailure(error: unknown): void {
    if (error instanceof ServiceError) setFailure(error)
  }

  /* I fotogrammi arrivano a pagine, con i filtri della colonna sinistra.
   * Cambiare filtro riparte da zero; «mostra altri» accoda. */
  useEffect(() => {
    let alive = true
    coverage
      .frames(inspectionId, { ...coverage.EMPTY, ...filters, limit: coverage.PAGE, offset })
      .then((found) => {
        if (!alive) return
        setTotal(found.total)
        setCameras(found.cameras ?? [])
        setFrames((were) => (offset === 0 ? found.frames : [...were, ...found.frames]))
      })
      .catch(onFailure)
    return () => {
      alive = false
    }
  }, [inspectionId, filters, offset, reloads])

  useEffect(() => {
    let alive = true
    coverage
      .coverageMap(inspectionId)
      .then((found) => alive && setElements(found.elements))
      .catch(onFailure)
    return () => {
      alive = false
    }
  }, [inspectionId])

  /* Quando la casella si svuota di una dichiarazione, i conteggi del
   * fotogramma sono cambiati: si rilegge la pagina corrente da capo. */
  const unsentBefore = useRef(unsent)
  useEffect(() => {
    if (unsent < unsentBefore.current) setReloads((was) => was + 1)
    unsentBefore.current = unsent
  }, [unsent])

  useEffect(() => {
    if (session !== null) return
    let alive = true
    openSession(inspectionId).then(
      (opened) => {
        if (!alive) return
        setSession(opened.review_session_id)
        setEpoch(opened.review_epoch)
      },
      () => undefined,
    )
    return () => {
      alive = false
    }
  }, [inspectionId, session])

  useEffect(() => {
    if (session === null) return
    const timer = setInterval(() => {
      heartbeat(session).then(
        (alive) => setEpoch(alive.review_epoch),
        () => undefined,
      )
    }, HEARTBEAT_MS)
    return () => clearInterval(timer)
  }, [session])

  useEffect(() => {
    let alive = true
    void posta.declarations().then((found) => alive && setDeclarations(found))
    return () => {
      alive = false
    }
  }, [posta, unsent, refused])

  useEffect(() => {
    void posta.flush()
    const again = (): void => void posta.flush()
    window.addEventListener('online', again)
    return () => window.removeEventListener('online', again)
  }, [posta])

  const current = frames.find((one) => one.photo_id === chosen) ?? frames[0] ?? null
  /* La ricerca rilegge l'elenco a ogni lettera, e ogni rilettura porta
   * collegamenti firmati nuovi per lo stesso fotogramma: il visualizzatore si
   * rifarebbe a ogni battuta. Finche' il fotogramma scelto e' lo stesso, si
   * tiene il primo collegamento. */
  const shown = useRef<{ photoId: string; tilesUrl: string | null } | null>(null)
  if (current === null) {
    shown.current = null
  } else if (shown.current === null || shown.current.photoId !== current.photo_id) {
    shown.current = { photoId: current.photo_id, tilesUrl: current.tiles_url }
  }
  const tilesUrl = shown.current?.tilesUrl ?? null
  // I vicini nell'elenco si scaldano mentre si guarda questo (GI-257).
  useEffect(() => {
    if (current === null) return
    const where = frames.findIndex((one) => one.photo_id === current.photo_id)
    for (const near of [frames[where - 1], frames[where + 1]]) {
      if (near?.tiles_url) void prewarm(near.tiles_url, indexPath(near.photo_id))
    }
  }, [current, frames])
  const currentId = current?.photo_id ?? null
  // Solo gli elementi che *questo* fotogramma inquadra: offrire l'anagrafica
  // intera inviterebbe a dichiarare un'anomalia su una campata che non e' nello
  // scatto, e il servizio la scriverebbe senza accorgersene.
  // Le due righe che prima erano un trattino, con la loro origine accanto.
  const taken = current === null ? null : takenFact(current, t)
  const quality =
    current === null ? null : qualityFact(current, (current.elements ?? 0) > 0 || current.quality != null, t)
  const framed = useMemo(() => {
    const allowed = new Set(current?.element_ids ?? [])
    return elements.filter((one) => allowed.has(one.element_id))
  }, [current, elements])

  useEffect(() => {
    // Cambiando fotogramma l'elemento scelto puo' non esserci piu': tenerlo
    // manderebbe la dichiarazione su una campata che non si sta guardando.
    if (framed.some((one) => one.element_id === elementId)) return
    setElementId(framed[0]?.element_id ?? '')
  }, [framed, elementId])

  useEffect(() => {
    if (elementId === '') {
      setCodes([])
      setCode('')
      return
    }
    let alive = true
    coverage
      .catalogFor(inspectionId, elementId)
      .then((found) => {
        if (!alive) return
        setCodes(found)
        setCode(found[0]?.code ?? '')
      })
      .catch(() => alive && setCodes([]))
    return () => {
      alive = false
    }
  }, [inspectionId, elementId])

  useEffect(() => {
    const allowed = levelsOf(codes, code)
    if (allowed.length > 0 && !allowed.includes(level)) setLevel(allowed[0] ?? 1)
  }, [codes, code, level])

  function record(): void {
    if (current === null) return
    if (elementId === '') {
      setWrong(t('search.elementRequired'))
      return
    }
    if (!(current.element_ids ?? []).includes(elementId)) {
      setWrong(t('search.notFramed'))
      return
    }
    if (code === '') {
      setWrong(t('coverage.codeRequired'))
      return
    }
    const outcome = boxOf(draft, current)
    if ('error' in outcome) {
      setWrong(outcome.error)
      return
    }
    if (now.current === null) {
      setWrong(t('search.noSession'))
      return
    }
    setWrong(null)
    posta.add({
      kind: 'manual_finding',
      photoId: current.photo_id,
      elementId,
      code,
      level,
      box: outcome.box,
    })
    setSaid(
      t('search.findingQueued', { code, level, filename: current.filename }),
    )
    setDraft(NO_BOX)
  }

  function markReviewed(frame: Frame): void {
    if (now.current === null) {
      setWrong(t('search.noSession'))
      return
    }
    const reviewed = frame.reviewed !== true
    posta.add({
      kind: 'photo_reviewed',
      photoId: frame.photo_id,
      reviewed,
      expectedCandidateVersion: frame.review_sequence ?? 0,
    })
    setSaid(
      reviewed
        ? t('coverage.reviewedDeclared', { filename: frame.filename })
        : t('coverage.reviewedWithdrawn', { filename: frame.filename }),
    )
  }

  /* Le anomalie del fotogramma scelto, per disegnarle e per elencarle. Si
   * rileggono anche quando una dichiarazione parte: la nuova e' fra quelle. */
  useEffect(() => {
    if (currentId === null) {
      setFindings([])
      return
    }
    let alive = true
    coverage
      .frameFindings(inspectionId, currentId)
      .then((found) => alive && setFindings(found.findings))
      .catch(() => alive && setFindings([]))
    return () => {
      alive = false
    }
  }, [inspectionId, currentId, reloads])

  /* Il nome battuto diventa filtro con un respiro: una richiesta per lettera
   * sarebbe una coda al servizio per scrivere «RETRO». */
  useEffect(() => {
    const timer = setTimeout(() => {
      const q = typed.trim() === '' ? null : typed.trim()
      setFilters((were) => (were.q === q ? were : { ...were, q }))
      setOffset(0)
    }, 250)
    return () => clearTimeout(timer)
  }, [typed])

  function filter(change: Partial<Filters>): void {
    setFilters((were) => ({ ...were, ...change }))
    setOffset(0)
  }

  function pick(frame: Frame): void {
    setChosen(frame.photo_id)
    setDraft(NO_BOX)
    setWrong(null)
  }

  if (failure) {
    return (
      <main>
        <h2>{t('search.unread')}</h2>
        <p role="alert">{failure.message}</p>
        {failure.requestId && (
          <p>
            <small>{t('pool.requestId', { id: failure.requestId })}</small>
          </p>
        )}
      </main>
    )
  }

  const box = boxOf(draft, current)
  const declared = current === null
    ? undefined
    : declarations.get(subjectOf({ kind: 'photo_reviewed', photoId: current.photo_id }))
  // Per identita' stabile: un elenco nuovo a ogni ridisegno farebbe rifare il
  // visualizzatore con gli stessi segni.
  const marks: Mark[] = useMemo(
    () =>
      findings
        .filter((one): one is FrameFinding & { box: NonNullable<FrameFinding['box']> } => one.box !== null)
        .map((one) => ({
          box: one.box,
          label: `${one.code} · ${rankOf(one.final_level ?? one.level).name}`,
          tone: one.decision === 'rejected' ? 'ink-3' : rankOf(one.final_level ?? one.level).tone,
        })),
    [findings],
  )

  /** Gli strumenti della tela, dal bottone e dal tasto: la stessa azione, e
   *  qui solo quelle che hanno senso su un fotogramma senza candidato. */
  function act(action: Action): boolean {
    if (!action) return false
    if (action.kind === 'draw') {
      setSketching((was) => !was)
      return true
    }
    if (action.kind === 'cancel') {
      if (!sketching) return false
      setSketching(false)
      return true
    }
    if (action.kind === 'zoom') {
      setView((was) => ({ how: action.how, nonce: (was?.nonce ?? 0) + 1 }))
      return true
    }
    if (action.kind === 'overlays') {
      setHidden(!action.visible)
      return true
    }
    return false
  }

  /* Il fuoco va alla schermata quando il fotogramma arriva, se nessuno lo ha
   * chiesto altrove — la shell lo mette sul titolo per annunciare la route, e da
   * li' `B` e `X` non arriverebbero a niente. Lo stesso gesto del banco. */
  const root = useRef<HTMLDivElement | null>(null)
  const grabbed = useRef(false)
  useEffect(() => {
    if (grabbed.current || current === null) return
    const active = document.activeElement
    const idle = active === null || active === document.body || active.tagName === 'H1'
    if (!idle) return
    grabbed.current = true
    root.current?.focus()
  }, [current])

  return (
    <div
      className="photos-view"
      data-theme="cockpit"
      ref={root}
      tabIndex={-1}
      onKeyDown={(event) => {
        // Gli stessi confini del banco: i campi restano campi, i comandi col
        // fuoco prendono spazio e invio, e passa solo cio' che qui fa qualcosa
        // — il resto della mappa (verdetti, coda) non esiste su questa schermata
        // e non deve rubare un tasto al navigatore.
        if (editable(event.target) || commanding(event.target)) return
        if (event.repeat && event.key !== ' ') return
        if (act(actionFor(event, 'normale'))) event.preventDefault()
      }}
      onKeyUp={(event) => {
        if (event.key === ' ' && !editable(event.target) && !commanding(event.target)) setHidden(false)
      }}
    >
      <aside className="photos-side photos-left" aria-label={t('photos.list')}>
        <p className="photos-search">
          <label htmlFor="photos-q" className="solo-lettori">
            {t('photos.search')}
          </label>
          <input
            id="photos-q"
            type="search"
            placeholder={t('photos.search')}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </p>
        <div className="photos-cameras" role="group" aria-label={t('photos.camera')}>
          <button
            type="button"
            aria-pressed={filters.camera === null}
            onClick={() => filter({ camera: null })}
          >
            {t('photos.cameraAll')}{' '}
            <span className="tenue t-num">{cameras.reduce((sum, one) => sum + one.frames, 0)}</span>
          </button>
          {cameras
            .filter((one) => one.camera !== null)
            .map((one) => (
              <button
                key={one.camera}
                type="button"
                aria-pressed={filters.camera === one.camera}
                onClick={() => filter({ camera: one.camera })}
              >
                {one.camera} <span className="tenue t-num">{one.frames}</span>
              </button>
            ))}
        </div>
        <p className="photos-only">
          <label>
            <input
              type="checkbox"
              checked={filters.reviewed === false}
              onChange={(event) => filter({ reviewed: event.target.checked ? false : null })}
            />{' '}
            {t('photos.onlyUnreviewed')}
          </label>
          <label>
            <input
              type="checkbox"
              checked={filters.noCandidates === true}
              onChange={(event) => filter({ noCandidates: event.target.checked ? true : null })}
            />{' '}
            {t('photos.onlyUntouched')}
          </label>
        </p>
        <p className="tenue t-num photos-count" role="status">
          {t('photos.count', { shown: frames.length, total })}
          {unsent > 0 && <> · {t('coverage.toSend', { n: unsent })}</>}
        </p>
        <ol className="photos-list">
          {frames.map((frame) => {
            const on = current !== null && frame.photo_id === current.photo_id
            return (
              <li key={frame.photo_id}>
                <button
                  type="button"
                  className={on ? 'photos-row on' : 'photos-row'}
                  aria-current={on ? 'true' : undefined}
                  onClick={() => pick(frame)}
                >
                  {frame.thumbnail_url !== null ? (
                    <img className="photos-thumb" src={frame.thumbnail_url} alt="" loading="lazy" />
                  ) : (
                    <span className="photos-thumb photos-thumb-none" aria-hidden="true" />
                  )}
                  <span className="photos-name">{frame.filename}</span>
                  <span className="photos-badges t-num">
                    {frame.findings > 0 && <span className="photos-badge">{frame.findings}</span>}
                    {frame.reviewed === true && (
                      <span className="photos-badge photos-ok" title={t('photos.reviewed')}>
                        ✓
                      </span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ol>
        {frames.length < total && (
          <button type="button" className="photos-more" onClick={() => setOffset(frames.length)}>
            {t('photos.loadMore', { n: Math.min(coverage.PAGE, total - frames.length) })}
          </button>
        )}
        <p className="tenue">
          <Link to={`/inspections/${inspectionId}/coverage`}>{t('search.openCoverage')}</Link>
        </p>
      </aside>

      <section className="photos-canvas" aria-label={t('coverage.colFrame')}>
        {current && tilesUrl && current.width && current.height ? (
          <>
            <CanvasTools
              drawing={sketching ? 'box' : null}
              hidden={hidden}
              vertices={0}
              disabled={false}
              drawable
              withPolygon={false}
              withFinding={false}
              hint={t('photos.canvasHint')}
              onAction={act}
            />
            <Viewer
              tilesUrl={tilesUrl}
              indexUrl={indexPath(current.photo_id)}
              width={current.width}
              height={current.height}
              tile={current.tile ?? 512}
              levels={current.levels ?? 1}
              box={'box' in box ? box.box : null}
              marks={marks}
              drawing={sketching}
              onDrawn={(drawn) => {
                setDraft(draftOf(drawn))
                setSketching(false)
                setDeclaring(true)
              }}
              mode={sketching ? t('cockpit.modeDraw') : null}
              hidden={hidden}
              view={view}
              frame={{
                camera: current.camera ?? null,
                filename: current.filename,
                when: current.taken_at == null ? null : taken?.value ?? null,
              }}
              alt={t('coverage.altFrame', { filename: current.filename })}
            />
          </>
        ) : (
          <p className="tenue photos-empty" role="status">
            {current === null
              ? frames.length === 0
                ? t('search.noFrames')
                : t('photos.chooseFrame')
              : t('search.noPyramid', { filename: current.filename })}
          </p>
        )}
      </section>

      <aside className="photos-side photos-right" aria-label={t('photos.detail')}>
        {said !== null && <p role="status">{said}</p>}
        {refused.map((one, index) => (
          <p role="alert" key={`${one.code ?? 'rifiuto'}-${index}`}>
            {one.message}
          </p>
        ))}

        {current !== null && (
          <>
            <h2 className="photos-title">{current.filename}</h2>
            <dl className="photos-facts">
              <dt>{t('photos.camera')}</dt>
              <dd>{current.camera ?? '—'}</dd>
              <dt>{t('photos.taken')}</dt>
              <dd className="t-num">
                {taken?.value}
                {taken?.note != null && <span className="tenue"> · {taken.note}</span>}
              </dd>
              <dt>{t('photos.size')}</dt>
              <dd className="t-num">
                {current.width !== null && current.height !== null
                  ? `${current.width} × ${current.height}`
                  : '—'}
              </dd>
              <dt>{t('photos.quality')}</dt>
              <dd className="t-num">
                {quality?.value}
                {quality?.note != null && <span className="tenue"> · {quality.note}</span>}
              </dd>
              <dt>{t('photos.elements')}</dt>
              <dd className="t-num">{framed.length}</dd>
            </dl>
            <p className="tenue">
              {current.reviewed === true && t('photos.reviewed')}
              {declared !== undefined && <> · {t('coverage.deliveryPending')}</>}
            </p>

            {/* Dal fotogramma all'elemento e alla mappa in un gesto, e da qui
                a tutti i fotogrammi dello stesso sostegno (GI-248). */}
            {framed.length > 0 && (
              <ul className="photos-framed" aria-label={t('photos.elements')}>
                {framed.map((one) => (
                  <li key={one.element_id}>
                    <span className="t-num">{one.external_element_id}</span> · {one.element_type} ·{' '}
                    <Link to={`/inspections/${inspectionId}/map?element=${encodeURIComponent(one.element_id)}`}>
                      {t('photos.onMap')}
                    </Link>{' '}
                    ·{' '}
                    <button
                      type="button"
                      className="photos-link"
                      onClick={() => {
                        setOffset(0)
                        setFilters((were) => ({ ...were, element: one.element_id }))
                      }}
                    >
                      {t('photos.framesOfElement')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <h3>{t('photos.findings')}</h3>
            {findings.length === 0 ? (
              <p className="tenue">{t('photos.noFindings')}</p>
            ) : (
              <ul className="photos-findings">
                {findings.map((one) => {
                  const rank = rankOf(one.final_level ?? one.level)
                  return (
                    <li key={one.detection_id}>
                      <span className="glifo" style={{ color: `var(--${rank.tone})` }} aria-hidden="true">
                        {rank.glyph}
                      </span>{' '}
                      <span>{one.description === '' ? one.code : one.description}</span>{' '}
                      <span className="tenue">
                        {one.code} ·{' '}
                        {one.decision === null
                          ? t('photos.waiting')
                          : one.decision === 'confirmed'
                            ? t('photos.confirmed', { rank: rank.name })
                            : t('photos.rejected')}
                      </span>{' '}
                      <Link to={`/inspections/${inspectionId}/candidates/${one.detection_id}`}>
                        {t('photos.openDesk')}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            )}
          </>
        )}

        <details
          className="photos-declare"
          open={declaring}
          onToggle={(event) => setDeclaring((event.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>{t('photos.declare')}</summary>
          <label htmlFor="elemento-anomalia">{t('search.element')}</label>
          <select
            id="elemento-anomalia"
            value={elementId}
            onChange={(event) => setElementId(event.target.value)}
          >
            {framed.length === 0 && <option value="">{t('search.noFramed')}</option>}
            {framed.map((one) => (
              <option key={one.element_id} value={one.element_id}>
                {one.external_element_id} — {one.element_type}
              </option>
            ))}
          </select>

          <fieldset>
            <legend>{t('coverage.boxLegend')}</legend>
            <p className="tenue">{t('search.drawHint')}</p>
            <div className="photos-box-fields">
              {(['x', 'y', 'width', 'height'] as const).map((name) => (
                <span key={name}>
                  <label htmlFor={`riquadro-${name}`}>
                    {name === 'x'
                      ? t('coverage.boxX')
                      : name === 'y'
                        ? t('coverage.boxY')
                        : name === 'width'
                          ? t('coverage.boxWidth')
                          : t('coverage.boxHeight')}
                  </label>
                  <input
                    id={`riquadro-${name}`}
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    required
                    aria-describedby={wrong === null ? undefined : 'anomalia-errore'}
                    value={draft[name]}
                    onChange={(event) => setDraft({ ...draft, [name]: event.target.value })}
                  />
                </span>
              ))}
            </div>
            {/* Quanto misura sul terreno, dal dettaglio del fotogramma: una
                fessura di 4 cm e una zona di poroso di 30 m² si dichiarano
                con la stessa ramka, e il numero dice quale delle due e' (GI-259). */}
            {'box' in box && current !== null && (
              <p className="tenue photos-extent t-num">
                {(() => {
                  const extent = extentOf(box.box, current.quality)
                  return extent === null ? t('photos.extentUnknown') : t('photos.extent', { size: extentText(extent) })
                })()}
              </p>
            )}
          </fieldset>

          <label htmlFor="codice-anomalia">{t('coverage.code')}</label>
          <select id="codice-anomalia" value={code} onChange={(event) => setCode(event.target.value)}>
            {codes.length === 0 && <option value="">{t('coverage.noCodes')}</option>}
            {codes.map((one) => (
              <option key={one.code} value={one.code}>
                {one.code} — {one.description}
              </option>
            ))}
          </select>

          <label htmlFor="livello-anomalia">{t('coverage.level')}</label>
          <select
            id="livello-anomalia"
            value={level}
            onChange={(event) => setLevel(Number(event.target.value))}
          >
            {levelsOf(codes, code).map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </select>

          {wrong !== null && (
            <p role="alert" id="anomalia-errore">
              {wrong}
            </p>
          )}

          <button type="button" onClick={record}>
            {t('coverage.record')}
          </button>
          <p className="tenue">
            {/* Va detto: chi trova a mano non sta correggendo il modello, e il
                conteggio delle sue correzioni non deve gonfiarsi. */}
            {t('coverage.recordNote')}
          </p>
        </details>

        {/* Il nome del file sta nel titolo, non nella scritta: nel bottone
            andava a capo e rompeva il ritmo del pannello (GI-253). */}
        {current !== null && (
          <button type="button" title={current.filename} onClick={() => markReviewed(current)}>
            {current.reviewed === true ? t('coverage.withdrawReview') : t('coverage.declareReviewed')}
          </button>
        )}
      </aside>
    </div>
  )
}
